import type {
    LanguageEnum,
    LanguageLocalesEnum,
    TranscriptWordRequest,
    TranslatedTranscript,
} from 'js/src/client';
import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    hasLoadedSupportedLanguages,
    storeSupportedLanguages,
    storeVideoTranslations,
} from 'js/src/data/translations';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';
import WebSocketClient from 'js/src/libs/websocket-client';

export function loadVideoTranslations(videoId: number, videoSlug: string | null) {
    return (dispatch: AppDispatch) => {
        ApiService.videoTranslatedTextList({
            path: { video_id: videoId.toString() },
            query: { video_slug: videoSlug ?? undefined },
        })
            .then((response) => {
                dispatch(
                    storeVideoTranslations({
                        translations: response.data,
                    }),
                );
            })
            .catch(() => {
                Toast.error('Error loading video translations. Please try again.');
            });
    };
}

export async function translateText(
    transcript: TranscriptWordRequest[],
    language: LanguageLocalesEnum,
    original_language: LanguageEnum | undefined,
    videoId: number | null,
) {
    const ws = WebSocketClient.getInstance();
    const response = await ApiService.textTranslateCreate({
        body: {
            target_language: language,
            original_language,
            original_transcript: transcript,
            video_id: videoId,
        },
    });
    const translated_transcript = await ws.waitForMessage<TranslatedTranscript>(
        response.data.task_id,
        TaskResultMessageTypeEnum.TASK_RESULT,
        'translate_transcript',
    );
    return translated_transcript.translated_transcript;
}

let supportedLanguagesLoadPromise: Promise<void> | null = null;

export function loadSupportedLanguages() {
    return async (dispatch: AppDispatch, getState: GetRootState): Promise<void> => {
        if (hasLoadedSupportedLanguages(getState())) {
            return;
        }
        if (supportedLanguagesLoadPromise) {
            return supportedLanguagesLoadPromise;
        }
        supportedLanguagesLoadPromise = ApiService.supportedLanguagesList()
            .then((response) => {
                dispatch(storeSupportedLanguages({ supported_languages: response.data }));
            })
            .catch((err: unknown) => {
                ErrorReporter.capture(err);
            });
        return supportedLanguagesLoadPromise;
    };
}
